import React, { useEffect, useState } from "react";
import { Button, Progress, Badge, Alert, Group, Stack, Text, Divider, Paper } from "@mantine/core";
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import flat from "flat";
import { addDays, differenceInDays, isValid, parseISO, subDays } from "date-fns";
import { 
  IconCloudUpload, IconDownload, IconFileCheck, IconTrash, IconInfoCircle 
} from "@tabler/icons-react";

// Utilities & APIs
import { selectUserData } from "../../../redux/selectors";
import { handleChangeUpload } from "../BulkAgreement/utils";
import { createAgreement, getDocsToken, putLegacyFlowJson } from "../../../utilities/apis/agreements";
import { attachFileToParent } from "../../../utilities/apis/commonApis";
import { apiWrapWithErrorWithData } from "../../../utilities/apiHelpers";
import constants from "../../../utilities/constants";
import UniversalTable from "../../../components/Common/UniversalTable";

const CreateBulkLegacyAgreement = () => {
  const [value, setValue] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [flowJsonData, setFlowJsonData] = useState({});
  const [parsedData, setParsedData] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const currentUser = useSelector(selectUserData);
  const navigate = useNavigate();

  // 1. STRICT COLUMN ORDER
  const allColumns = [
    "typeOfAgreement", "relationshipType", "relationshipWith", "titleOfAgreement",
    "requestingUserName", "requestingUserEmail", "requestingUserPhone",
    "requestingUserDesignation", "requestingUserDepartment", "dateOfContract",
    "contractEndDate", "noOfYears", "noOfMonths", "reminderTimeInDays", "location",
    "firstPartyType", "firstPartyEntityType", "firstPartyName",
    "secondPartyType", "secondPartyEntityType", "secondPartyName",
    "fileName"
  ];

  const mandatoryFields = [
    "typeOfAgreement", "relationshipType", "titleOfAgreement",
    "requestingUserName", "requestingUserEmail", "dateOfContract",
    "reminderTimeInDays", "firstPartyType", "firstPartyName",
    "secondPartyType", "secondPartyName", "fileName"
  ];

  useEffect(() => {
    const roleId = currentUser?.loginAs?.id;
    if (roleId === 3 || roleId === 4) {
      const initialFlow = { 3: [], 4: [], 5: [], 6: [], 7: [] };
      initialFlow[roleId] = [{ userId: currentUser.id, isLegacy: true, isComplete: true }];
      setFlowJsonData(initialFlow);
    }
  }, [currentUser]);

  const parseLegacyDate = (value) => {
    if (!value) return null;
    const parts = value.toString().trim().split("-");
    if (parts.length !== 3) return null;
    const [day, month, year] = parts;
    if (!day || !month || !year) return null;
    const isoValue = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T00:00:00.000Z`;
    const parsed = parseISO(isoValue);
    return isValid(parsed) ? isoValue : null;
  };

  const handleDownloadTemplate = () => {
    const rows = [
      "INSTRUCTIONS: Row 3 is the header. Dates must be DD-MM-YYYY. Provide either duration (No Of Years/No Of Months) or Contract End Date. If both are provided, Contract End Date is used. Do not leave mandatory fields empty. One example is added for reference.",
      "EXAMPLE: Master Agreement,Parent,,Project X,John Doe,john@email.com,,,,01-01-2025,31-12-2025,1,0,30,Dubai,Entity,Company/LLP,Entity Name,Individual/Entity,Company/LLP,Individual/Entity Name,contract.pdf",
      allColumns.join(",")
    ];
    const csvContent = "data:text/csv;charset=utf-8," + rows.join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "bulk_legacy_template.csv");
    link.click();
  };

  const handleClear = () => {
    setParsedData([]);
    setAttachedFiles([]);
    setUploadStatus('');
    setValue(0);
    const fileInput = document.getElementById('bulk-file-input');
    if (fileInput) fileInput.value = "";
  };


  const handleFileChange = async (e) => {
  const allFiles = Array.from(e.target.files);
  const csvFile = allFiles.find(f => f.name.toLowerCase().endsWith('.csv'));
  const pdfFiles = allFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));

  if (!csvFile) {
    setUploadStatus('Error: Please select a CSV file.');
    return;
  }

  const reader = new FileReader();
  reader.onload = async (event) => {
    const text = event.target.result;
    const lines = text.split(/\r?\n/); // Split by new line

    // 1. Find the index of the header row
    const headerIndex = lines.findIndex(line => line.includes("typeOfAgreement"));

    if (headerIndex === -1) {
      setUploadStatus("Error: Could not find 'typeOfAgreement' header in CSV.");
      return;
    }

    // 2. Extract the Headers
    const headers = lines[headerIndex].split(",").map(h => h.trim());
    const normalizeHeader = (header) => header.toLowerCase().replace(/\s/g, "");
    const headerLookup = headers.reduce((acc, header) => {
      acc[normalizeHeader(header)] = header;
      return acc;
    }, {});

    // 3. Extract the Data Rows (skipping everything above and including the header)
    const dataLines = lines.slice(headerIndex + 1);
    
    const validatedRows = dataLines.reduce((acc, line, index) => {
      if (!line.trim()) return acc; // Skip empty lines

      const values = line.split(",").map(v => v.trim());
      
      // Map values to header keys
      const row = {};
      headers.forEach((key, i) => {
        row[key] = values[i] || "";
      });

      // Skip the Example row if the user left it in
      if (row.typeOfAgreement?.toLowerCase().includes("example")) return acc;

      const endDateHeader = headerLookup["contractenddate"];
      const yearsHeader = headerLookup["noofyears"];
      const monthsHeader = headerLookup["noofmonths"];
      const dateHeader = headerLookup["dateofcontract"] || "dateOfContract";

      const endDateValue = endDateHeader ? row[endDateHeader] : "";
      const yearsValue = yearsHeader ? parseInt(row[yearsHeader] || 0, 10) : 0;
      const monthsValue = monthsHeader ? parseInt(row[monthsHeader] || 0, 10) : 0;

      const startDateIso = parseLegacyDate(row[dateHeader]);
      const endDateIso = parseLegacyDate(endDateValue);

      const hasDuration = yearsValue > 0 || monthsValue > 0;
      const hasEndDate = Boolean(endDateIso);

      // Validation
      const missing = mandatoryFields.filter(f => !row[f] || row[f].toString().trim() === "");
      if (row[dateHeader] && !startDateIso) {
        missing.push("dateOfContract");
      }
      if (endDateValue && !endDateIso) {
        missing.push("contractEndDate");
      }
      if (hasEndDate && startDateIso && parseISO(endDateIso) <= parseISO(startDateIso)) {
        missing.push("contractEndDate");
      }
      if (!hasDuration && !hasEndDate) {
        missing.push("noOfYears/noOfMonths or contractEndDate");
      }
      const fileMatch = pdfFiles.some(f => f.name === row.fileName);

      acc.push({
        ...row,
        validation: {
          isValid: missing.length === 0 && fileMatch,
          missingFields: missing,
          fileMissing: !fileMatch
        }
      });
      return acc;
    }, []);

    setParsedData(validatedRows);
    setAttachedFiles(pdfFiles);
    setUploadStatus(validatedRows.length > 0 
      ? `Found ${validatedRows.length} records and ${pdfFiles.length} PDFs.` 
      : "Header found, but no data rows detected.");
  };

  reader.readAsText(csvFile);
};

const processUploads = async () => {
  const validRows = parsedData.filter((r) => r.validation.isValid);
  if (validRows.length === 0) return;

  setIsProcessing(true);
  try {
    // 1. Get the Microsoft Graph Access Token
    const tokenResp = await apiWrapWithErrorWithData(getDocsToken());
    const accessToken = tokenResp?.data || tokenResp?.token;

    if (!accessToken) {
      setUploadStatus("Error: Could not retrieve Microsoft Graph Token.");
      setIsProcessing(false);
      return;
    }

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      setUploadStatus(`Processing ${i + 1}/${validRows.length}: ${row.titleOfAgreement}`);

      // 2. Date & Term Calculations
      const formattedDate = parseLegacyDate(row.dateOfContract);
      if (!formattedDate) {
        setUploadStatus(`Error: Invalid dateOfContract for ${row.titleOfAgreement}`);
        continue;
      }

      const endDateIso = parseLegacyDate(row.contractEndDate);
      let totalDays = 0;
      let contractDueDate = null;
      if (endDateIso) {
        totalDays = differenceInDays(parseISO(endDateIso), parseISO(formattedDate));
        contractDueDate = parseISO(endDateIso);
      } else {
        totalDays = (parseInt(row.noOfMonths || 0, 10) * 30) + (parseInt(row.noOfYears || 0, 10) * 365);
        contractDueDate = addDays(parseISO(formattedDate), totalDays);
      }

      // 3. Construct DB Payload
      const dbPayload = {
        typeOfAgreement: row.typeOfAgreement,
        titleOfAgreement: row.titleOfAgreement,
        relationshipType: row.relationshipType,
        relationshipWith: row.relationshipWith || "",
        location: row.location || "",
        dateOfContract: formattedDate,
        contractTermInDays: totalDays,
        noOfYears: parseInt(row.noOfYears || 0, 10),
        noOfMonths: parseInt(row.noOfMonths || 0, 10),
        dueDateOfContract: contractDueDate,
        completionDate: formattedDate,
        reminderTimeInDays: parseInt(row.reminderTimeInDays || 0, 10),
        reminderTime: subDays(contractDueDate, parseInt(row.reminderTimeInDays || 0, 10)),
        requestingUserDetails: { name: row.requestingUserName, email: row.requestingUserEmail },
        firstParty: { name: row.firstPartyName, type: row.firstPartyType, entityType: row.firstPartyEntityType || "" },
        secondParty: { name: row.secondPartyName, type: row.secondPartyType, entityType: row.secondPartyEntityType || "" },
        businessUnit: currentUser.mapBusinessunit?.toString() || "1",
        status: "SIGNED",
        formCompleted: true,
      };

      // 4. API: Create Agreement Record
      const respo = await apiWrapWithErrorWithData(createAgreement({
        agreementData: {
          json: flat.unflatten({ ...dbPayload, isLegacy: true }),
          flatted: { ...dbPayload, isLegacy: true },
        },
        ...dbPayload
      }));

      if (respo?.agreement?.id) {
        // 5. API: Set Flow JSON
        await apiWrapWithErrorWithData(putLegacyFlowJson(respo.agreement.id, flowJsonData));

        // 6. MICROSOFT GRAPH: Upload PDF to OneDrive
        const matchedFile = attachedFiles.find(f => f.name === row.fileName);
        if (matchedFile) {
          setUploadStatus(`Uploading to OneDrive: ${matchedFile.name}...`);
          
          // Construct the OneDrive Upload URL
          const uploadURL = `https://graph.microsoft.com/v1.0/users/${constants.userId}/drive/root:/Bulk_Legacy/${matchedFile.name}:/content`;
          
          const uploadResponse = await fetch(uploadURL, {
            method: "PUT",
            headers: { 
              Authorization: `Bearer ${accessToken}`, 
              "Content-Type": "application/pdf" 
            },
            body: matchedFile, // Sending the actual file blob
          });

          if (uploadResponse.ok) {
            const driveData = await uploadResponse.json();
            
            // 7. API: Link the OneDrive File ID to the Database Record
            await apiWrapWithErrorWithData(attachFileToParent({
              parent: "agreement",
              parentId: respo.agreement.id.toString(),
              fileName: matchedFile.name,
              extension: "pdf",
              mimeType: "application/pdf",
              hostedLink: driveData.id // The ID returned by Microsoft Graph
            }));
          } else {
            console.error("Microsoft Graph Upload Failed", await uploadResponse.text());
          }
        }
      }
      setValue(((i + 1) / validRows.length) * 100);
    }

    setUploadStatus("Bulk Processing Successful!");
    setTimeout(() => navigate("/app/agreements_manager/docs-locker"), 1500);
  } catch (error) {
    setUploadStatus(`Error: ${error.message}`);
  } finally {
    setIsProcessing(false);
  }
};

  const validCount = parsedData.filter(r => r.validation.isValid).length;
  const errorCount = parsedData.length - validCount;

  return (
    <div className="p-10 bg-slate-50 min-h-screen">
      <Stack spacing="xl">
        <Paper p="lg" withBorder radius="md" bg="white">
          <Group position="apart">
            <div>
              <Text size="xl" weight={800}>Legacy Bulk Upload</Text>
              <Text size="sm" color="dimmed">Follow the template strictly for best results.</Text>
            </div>
            <Group>
              <Button variant="subtle" color="red" onClick={handleClear} disabled={parsedData.length === 0}>Clear All</Button>
              <Button style={{backgroundColor: "#1b6f8d"}} leftIcon={<IconDownload size={16}/>} onClick={handleDownloadTemplate}>Download Template</Button>
            </Group>
          </Group>
        </Paper>

        {!parsedData.length ? (
          <div className="h-64 border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center bg-white hover:border-blue-400 transition-colors">
            <IconCloudUpload size={48} color="#1b6f8d" className="mb-4 opacity-40" />
            <input type="file" id="bulk-file-input" multiple className="hidden" accept=".csv, .pdf" onChange={handleFileChange} />
            <label htmlFor="bulk-file-input">
              <Button component="span" size="md" style={{backgroundColor: "#1b6f8d"}}>Upload CSV & PDFs</Button>
            </label>
            <Text size="xs" color="dimmed" mt="sm">Select your CSV and all PDFs at once.</Text>
          </div>
        ) : (
          <>
            <Group grow mb="md">
              <Paper p="sm" withBorder><Text size="xs" color="dimmed">TOTAL ROWS</Text><Text size="lg" weight={700}>{parsedData.length}</Text></Paper>
              <Paper p="sm" withBorder><Text size="xs" color="dimmed">VALID</Text><Text size="lg" weight={700} color="green">{validCount}</Text></Paper>
              <Paper p="sm" withBorder><Text size="xs" color="dimmed">INVALID</Text><Text size="lg" weight={700} color="red">{errorCount}</Text></Paper>
            </Group>

            <UniversalTable
              headers={[
                { label: "Title", key: "titleOfAgreement" },
                { label: "File Name", key: "fileName" },
                { label: "Status", render: (r) => <Badge color={r.validation.isValid ? "green" : "red"}>{r.validation.isValid ? "Valid" : "Error"}</Badge> },
                { label: "Issues", render: (r) => (
                  <Stack spacing={0}>
                    {r.validation.fileMissing && <Text size="xs" color="red">• PDF Missing</Text>}
                    {r.validation.missingFields.map(f => <Text key={f} size="xs" color="orange">• {f} Empty</Text>)}
                  </Stack>
                )}
              ]} 
              data={parsedData} 
              loading={false} 
            />

            <Paper p="md" withBorder bg="white">
              <Group position="apart">
                <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="transparent" className="p-0">
                  <Text size="xs">Only the <b>{validCount} valid</b> records will be imported. Errors will be ignored.</Text>
                </Alert>
                <Button 
                  size="md" 
                  style={{backgroundColor: "#1b6f8d"}} 
                  onClick={processUploads} 
                  loading={isProcessing}
                  disabled={validCount === 0}
                  leftIcon={<IconFileCheck size={20}/>}
                >
                  Upload {validCount} Agreements
                </Button>
              </Group>
            </Paper>
          </>
        )}

        {isProcessing && (
          <Paper p="md" withBorder>
            <Text size="sm" mb="xs" weight={600}>{uploadStatus}</Text>
            <Progress value={value} size="xl" radius="xl" striped animated color="blue" />
          </Paper>
        )}
      </Stack>
    </div>
  );
};

export default CreateBulkLegacyAgreement;